import {
  InputHTMLAttributes,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState
} from 'react'
import clsx from 'clsx'

import styles from './TextField.module.scss'

export interface TextFieldProps
  extends Omit<
    InputHTMLAttributes<HTMLDivElement>,
    'size' | 'onChange' | 'onBlur' | 'onFocus' | 'onKeyDown'
  > {
  id?: string
  name?: string
  value?: string | number
  label?: React.ReactNode
  placeholder?: string
  error?: boolean
  helperText?: React.ReactNode
  disabled?: boolean
  required?: boolean
  marginBottom?: boolean
  rows?: number
  size?: 'medium' | 'large'
  startAdornment?: React.ReactElement | null
  endAdornment?: React.ReactElement | null
  icons?: {
    start?: React.ReactElement | null
    end?: React.ReactElement | null
  }
  className?: string
  inputProps?: InputHTMLAttributes<HTMLInputElement | HTMLTextAreaElement>
  textHelperProps?: React.HTMLAttributes<HTMLDivElement>
  ref?: React.Ref<HTMLInputElement | HTMLTextAreaElement>
  onChange?: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void
  onBlur?: (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void
  onFocus?: (
    e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void
  onKeyDown?: (
    e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => unknown
}

const TextField = ({
  id,
  name,
  value,
  label,
  placeholder,
  error = false,
  helperText,
  disabled = false,
  required = false,
  size = 'large',
  startAdornment,
  endAdornment,
  icons,
  inputProps,
  textHelperProps,
  onChange,
  onBlur,
  onFocus,
  onKeyDown,
  marginBottom = true,
  type,
  className,
  rows,
  ref,
  ...props
}: TextFieldProps) => {
  const [isFocused, setIsFocused] = useState(false)
  const [isFilled, setIsFilled] = useState(false)
  const internalRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null)
  const generatedId = useId()

  // Combine external ref with internal ref
  const inputRef = (ref || internalRef) as React.RefObject<
    HTMLInputElement | HTMLTextAreaElement
  >

  // Determine if component is controlled
  // Note: Boolean primitives are compared by value, not reference, so this won't cause unnecessary re-renders
  const isControlled = value !== undefined

  // Normalize value to string (fixes type safety issue)
  const normalizedValue = useMemo(() => {
    if (isControlled) {
      return value != null ? String(value) : ''
    }

    return undefined
  }, [value, isControlled])

  // Generate stable IDs
  // No `name` fallback: the same name can be mounted twice (e.g. web + mobile filters) and ids must stay unique
  const inputId = id || `textfield-${generatedId}`
  const helperTextId = `${inputId}-helper-text`
  const describedBy =
    clsx(helperText && helperTextId, inputProps?.['aria-describedby']) ||
    undefined

  useEffect(() => {
    if (isControlled) {
      setIsFilled(Boolean(normalizedValue && normalizedValue.length > 0))
    }
  }, [normalizedValue, isControlled])

  const handleFocus = useCallback(
    (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setIsFocused(true)
      onFocus?.(e)
    },
    [onFocus]
  )

  const handleBlur = useCallback(
    (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setIsFocused(false)
      setIsFilled(Boolean(e.target.value && e.target.value.length > 0))
      onBlur?.(e)
    },
    [onBlur]
  )

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setIsFilled(Boolean(e.target.value && e.target.value.length > 0))
      onChange?.(e)
    },
    [onChange]
  )

  const hasLabel = Boolean(label)
  const labelId = useMemo(
    () => (hasLabel ? `${inputId}-label` : undefined),
    [hasLabel, inputId]
  )

  const shouldShrinkLabel = useMemo(
    () => isFocused || isFilled || Boolean(placeholder),
    [isFocused, isFilled, placeholder]
  )

  const finalStartAdornment = useMemo(
    () => startAdornment ?? icons?.start ?? null,
    [startAdornment, icons?.start]
  )

  const finalEndAdornment = useMemo(
    () => endAdornment ?? icons?.end ?? null,
    [endAdornment, icons?.end]
  )

  const adornmentProps = useMemo(
    () => ({
      hasStart: Boolean(finalStartAdornment),
      hasEnd: Boolean(finalEndAdornment)
    }),
    [finalStartAdornment, finalEndAdornment]
  )

  const multilineRows = !!rows && rows > 1
  const isTextArea = type === 'textarea' || multilineRows
  const multiline = isTextArea && multilineRows

  return (
    <div
      className={clsx(
        'TextField_Wrapper',
        styles.wrapper,
        { [styles.noMarginBottom]: !marginBottom },
        className
      )}
      {...props}
    >
      <div className={styles.innerWrapper}>
        {hasLabel && (
          <label
            htmlFor={inputId}
            id={labelId}
            className={clsx('TextField_Label', styles.label, {
              [styles.labelMedium]: size === 'medium',
              [styles.labelShrink]: shouldShrinkLabel,
              [styles.labelDisabled]: disabled,
              [styles.labelError]: error
            })}
          >
            {label}
            {required && (
              <span aria-hidden='true' className={styles.requiredStar}>
                {' *'}
              </span>
            )}
          </label>
        )}
        <div
          className={clsx('TextField_InputContainer', styles.inputContainer, {
            [styles.containerMedium]: size === 'medium',
            [styles.containerMultiline]: multiline,
            [styles.containerFocused]: isFocused,
            [styles.containerError]: error,
            [styles.containerDisabled]: disabled
          })}
        >
          {finalStartAdornment && (
            <div
              aria-hidden='true'
              className={clsx(
                'TextField_Adornment-start',
                styles.adornment,
                styles.adornmentStart
              )}
            >
              {finalStartAdornment}
            </div>
          )}
          {isTextArea ? (
            <textarea
              ref={inputRef as React.RefObject<HTMLTextAreaElement>}
              placeholder={placeholder}
              value={normalizedValue}
              id={inputId}
              name={name}
              aria-invalid={Boolean(error)}
              required={required}
              rows={rows}
              {...inputProps}
              aria-describedby={describedBy}
              onBlur={handleBlur}
              onFocus={handleFocus}
              onChange={handleChange}
              onKeyDown={onKeyDown}
              className={clsx('TextField_TextArea', styles.textArea, {
                [styles.textAreaMedium]: size === 'medium',
                [styles.textAreaMultiline]: multiline,
                [styles.textAreaHasStart]: adornmentProps.hasStart,
                [styles.textAreaHasEnd]: adornmentProps.hasEnd
              })}
            />
          ) : (
            <input
              type={type}
              ref={inputRef as React.RefObject<HTMLInputElement>}
              placeholder={placeholder}
              value={normalizedValue}
              id={inputId}
              name={name}
              aria-invalid={Boolean(error)}
              required={required}
              onBlur={handleBlur}
              onFocus={handleFocus}
              onChange={handleChange}
              onKeyDown={onKeyDown}
              {...inputProps}
              aria-describedby={describedBy}
              className={clsx('TextField_Input', styles.input, {
                [styles.inputMedium]: size === 'medium',
                [styles.inputHasStart]: adornmentProps.hasStart,
                [styles.inputHasEnd]: adornmentProps.hasEnd
              })}
            />
          )}
          {finalEndAdornment && (
            <div
              className={clsx(
                'TextField_Adornment-end',
                styles.adornment,
                styles.adornmentEnd
              )}
            >
              {finalEndAdornment}
            </div>
          )}
        </div>
        {/* Always mounted polite live region, so messages are announced without interrupting the field name */}
        <div
          id={helperTextId}
          aria-live='polite'
          {...textHelperProps}
          className={clsx(
            'TextField_HelperText',
            styles.helperText,
            { [styles.helperTextError]: error },
            textHelperProps?.className
          )}
        >
          {helperText}
        </div>
      </div>
    </div>
  )
}

export default TextField
